import React, { useCallback, useEffect, useState } from "react";
import { History, X, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { ScanAPI } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const VERDICT_TONE = {
  true: "#22c55e",
  mostly_true: "#84cc16",
  mixed: "#f59e0b",
  mostly_false: "#fb7185",
  false: "#FF0033",
  unverifiable: "#94a3b8",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/**
 * Slide-out drawer on the LEFT showing the operator's recent scans.
 * Mounted in the scan workspace; a toggle in ChaosHeader opens it.
 * Auto-fetches the first time it opens; manual refresh button afterward.
 */
const OperatorHistoryPanel = ({ open, onClose, onSelect }) => {
  const { status } = useAuth();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (status !== "authed") {
      setScans([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { scans: rows } = await ScanAPI.recent();
      setScans(rows || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load history.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="Close history"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] transition-opacity"
          data-testid="history-backdrop"
        />
      )}
      <aside
        data-testid="operator-history-panel"
        aria-hidden={!open}
        className={`fixed top-0 left-0 z-50 h-[100dvh] w-[88vw] max-w-sm border-r border-white/[0.08] bg-[#020617] text-[#f1f5f9] flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <header
          className="flex items-center justify-between gap-3 px-5 pb-4 border-b border-white/[0.06]"
          style={{
            // Clear the iOS / Android status-bar inset so the title +
            // close icon are fully tappable even when the panel renders
            // edge-to-edge over the system chrome.
            paddingTop: "max(16px, env(safe-area-inset-top))",
          }}
        >
          <div className="flex items-center gap-2.5">
            <History size={14} className="text-[#FF0033]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#cbd5e1]">
              Operator Activity
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="history-refresh"
              onClick={load}
              disabled={loading || status !== "authed"}
              className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center text-[#94a3b8] hover:text-[#FF0033] disabled:opacity-40 transition-colors"
              aria-label="Refresh history"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              type="button"
              data-testid="history-close"
              onClick={onClose}
              className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center text-[#94a3b8] hover:text-[#FF0033] transition-colors"
              aria-label="Close history"
            >
              <X size={15} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {status !== "authed" ? (
            <div className="p-5 font-mono text-[12px] text-[#64748b] leading-relaxed">
              Sign in to start your operator activity ledger. Every scan is
              persisted to your account.
            </div>
          ) : loading ? (
            <div className="p-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-[#94a3b8]">
              <Loader2 size={13} className="animate-spin" />
              Fetching activity…
            </div>
          ) : error ? (
            <div className="p-5 font-mono text-[12px] text-[#FF0033]">
              {error}
            </div>
          ) : scans.length === 0 ? (
            <div className="p-5 font-mono text-[12px] text-[#64748b] leading-relaxed">
              No scans yet — run your first truth-scan to populate this ledger.
            </div>
          ) : (
            <ul data-testid="history-list" className="divide-y divide-white/[0.05]">
              {scans.map((s) => {
                const tone = VERDICT_TONE[s.verdict?.verdict] || VERDICT_TONE.unverifiable;
                return (
                  <li key={s.scan_id} data-testid={`history-item-${s.scan_id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelect?.(s);
                        onClose?.();
                      }}
                      className="w-full text-left px-5 py-4 hover:bg-white/[0.03] group transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3 mb-1.5 font-mono text-[10px] uppercase tracking-[0.22em]">
                        <span
                          className="font-bold"
                          style={{ color: tone }}
                        >
                          {(s.verdict?.verdict || "unverifiable").replace("_", " ")}
                        </span>
                        <span className="text-[#475569] tabular-nums">
                          {formatDate(s.created_at)}
                        </span>
                      </div>
                      <p className="text-[13px] text-[#f1f5f9] leading-snug break-words mb-1.5 line-clamp-2">
                        {s.query}
                      </p>
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#64748b] group-hover:text-[#FF0033] transition-colors">
                        View detail
                        <ArrowRight size={11} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
};

export default OperatorHistoryPanel;
